import { useToast } from '#app/components/Toasts/hooks/useToast.js';
import { type Toast as ToastObject } from '#app/components/Toasts/types.js';
import { resolveValueOrFunction } from '#app/components/Toasts/utils.js';
import { useAppConfiguration } from '#app/hooks/useAppConfiguration.js';
import {
  Body,
  Container,
  DismissButton,
  FloatingDismissButton,
  Message,
  MessageActionsColumn,
} from './Toast.styles.js';
import { ToastIcon } from './ToastIcon.js';
import { CloseIcon } from '@contra/icons/icons';
import { useIsomorphicLayoutEffect } from '@contra/react-hooks/useIsomorphicLayoutEffect';
import { useCallback, useRef } from 'react';

type ToastProps = {
  readonly offset: number;
  readonly onHeight: (toastId: string, height: number) => void;
  readonly toast: ToastObject;
};

export const Toast = ({ offset, onHeight, toast }: ToastProps) => {
  const { id } = toast;

  const { playwrightTest } = useAppConfiguration();

  const ref = useRef<HTMLDivElement>(null);

  useIsomorphicLayoutEffect(
    () => {
      if (ref.current) {
        const boundingRect = ref.current.getBoundingClientRect();

        onHeight(id, boundingRect.height);
      }
    },
    // NOTE: Leave `onHeight` out of dependencies. -@erictaylor

    [ref, id],
  );

  const actions = useToast();

  const handleDismiss = useCallback(
    () => {
      actions.dismiss(id);
    },
    // NOTE: Leave `actions` out of dependencies. Guaranteed stable. -@erictaylor

    [id],
  );

  const {
    ariaLive,
    autoDismiss = true,
    height,
    hideIcon,
    icon,
    message,
    role,
    type,
    visible,
  } = toast;

  return (
    <Container offset={offset}>
      <Body
        $height={height}
        $type={type}
        $visible={visible}
        aria-labelledby={`toast-${id}`}
        aria-live={ariaLive}
        ref={ref}
        role={role}
      >
        {hideIcon ? null : (
          <ToastIcon
            icon={icon}
            type={type}
          />
        )}
        <MessageActionsColumn>
          <Message id={`toast-${id}`}>
            {resolveValueOrFunction(message, toast)}
          </Message>
          {type === 'content' || playwrightTest ? (
            <FloatingDismissButton
              aria-label="Dismiss"
              onClick={handleDismiss}
            >
              <CloseIcon size={24} />
            </FloatingDismissButton>
          ) : null}
          {autoDismiss || type === 'content' ? null : (
            <DismissButton
              aria-label="Dismiss"
              onClick={handleDismiss}
              size="small"
              variant="V2TertiaryOutline"
            >
              Close
            </DismissButton>
          )}
        </MessageActionsColumn>
      </Body>
    </Container>
  );
};
